import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { OrganizationDependencyContainer } from '../../infrastructure/dependencies/OrganizationDependencyContainer';
import { Organization } from '../../domain/entities/Organization';
import { MyOrganizationUser, OrgConnection } from '../../domain/repositories/IOrganizationRepository';

export function useOrgDetail(orgId: string | undefined) {
  const { t } = useTranslation();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [users, setUsers] = useState<MyOrganizationUser[]>([]);
  const [connections, setConnections] = useState<OrgConnection[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Identifica la carga vigente. El hook lo consumen componentes que sobreviven a la navegación
  // (AdminHeader vive en AdminLayout, no en la página), así que una respuesta tardía de una org
  // que ya se abandonó tiene que descartarse en vez de pisar el estado actual.
  const requestIdRef = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++requestIdRef.current;

    // Sin orgId no hay detalle que mostrar: hay que limpiar explícitamente. Antes se hacía un
    // early-return y el detalle de la org anterior se quedaba pegado al volver a la lista
    // (/admin/organizations), porque el hook nunca se desmonta entre lista y detalle.
    if (!orgId) {
      setOrganization(null);
      setUsers([]);
      setConnections([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const orgRepo = OrganizationDependencyContainer.getOrganizationRepository();
      const result = await orgRepo.getOrgDetail(orgId);
      if (requestId !== requestIdRef.current) return;
      setOrganization(result.organization);
      setUsers(result.users);
      setConnections(result.connections);
    } catch {
      if (requestId !== requestIdRef.current) return;
      // Fallo de carga de página: el detalle crudo del backend (código o "Request failed with
      // status code N" de axios) no aporta nada al usuario y quedaba visible sin traducir. Se
      // muestra un mensaje genérico traducido; la página ofrece reintento vía reload().
      setError(t('common.load_error'));
    } finally {
      // Solo la carga vigente puede apagar el spinner: si ya la reemplazó otra, esa otra manda.
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
    // `t` se omite a propósito: react-i18next devuelve una identidad nueva por render e incluirla
    // recrearía `load` en bucle con el useEffect. Se cierra sobre la `t` del render actual, que
    // traduce igual de bien en el instante del error.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  useEffect(() => {
    load();
  }, [load]);

  return { organization, users, connections, isLoading, error, reload: load };
}
